import { useCallback, useMemo } from 'react';
import { atom, useAtomValue, useAtom, useSetAtom } from 'jotai';
import { PaginatedProject } from 'types/index';
import { DEFAULT_MAP_ZOOM } from 'utils/constants';
import { REGION_DEFAULT_COORDINATES } from 'utils/constants/location';
import type { BoundingBox } from 'types/map';

export type MapRef = google.maps.Map | null;

type CustomPolygon = google.maps.Polygon & {
  className?: string;
};

type MapRadiusPayload = {
  point: [number, number];
  radius: number;
};

const selectedMapTypeAtom = atom<string | null>(null);
const mapApiLoaded = atom(false);
const loading = atom(false);
const polygons = atom<string[]>([]);
const selectedMapMarkerAddress = atom<string>('');
const selectedClusterId = atom<string>('');
const polygonLiterals = atom<CustomPolygon[]>([]);
const selectedPolygonsList = atom<Record<string, string>>({});
const clusterPoints = atom<any>([]);
const mapReference = atom<MapRef | null>(null);
const projectList = atom<PaginatedProject[]>([]);
const parcelProjectsAtom = atom<PaginatedProject[]>([]);
const parcelProjectsLoadingAtom = atom(false);
const parcelPolygonsAtom = atom<any[]>([]);
const forceRefreshPolygonsAtom = atom<number>(0);
const mapRadius = atom<MapRadiusPayload>({
  point: [0, 0],
  radius: 0,
});
const filter = atom<boolean>(true);

const isScrollable = atom<boolean>(false);
const mapPin = atom<any>({
  longitude: null,
  latitude: null,
});
const zoneOverlayPolygons = atom<any>({});
const selectedPolygonBoundsOnMap = atom<BoundingBox>('');
const selectedPolygonId = atom<string>('');
const showAllProjectsWithinPolygon = atom<boolean>(false);
const mapCenter = atom<google.maps.LatLngLiteral>(
  REGION_DEFAULT_COORDINATES['us-austin'],
);
const mapZoom = atom<number>(DEFAULT_MAP_ZOOM);
const isPolygonDrawingEnabledAtom = atom<boolean>(false);
const hasPlacedFirstPolygonDrawingPointAtom = atom<boolean>(false);
const projectInsightsViewTypeAtom = atom<'map' | 'list'>('map');
const addressSearchAtom = atom<string>('');

// The saved-search id whose filters are currently reflected in the map's
// filter state. Selecting a saved search flips the group URL param
// synchronously, but that search's real filters only land after an async
// fetch resolves; until this catches up to the selected id, the map fetches
// are held back so they don't fire with the previous search's filters (see
// useAreGroupFiltersReady). Empty string = no group / "viewing all".
const appliedMapGroupIdAtom = atom<string>('');

export const useMapCenter = () => useAtom(mapCenter);
export const useMapZoom = () => useAtom(mapZoom);

export const isMapFilterVisibleState = () => useAtom(filter);

export const useSelectedMapMarkerAddress = () => useAtom(selectedMapMarkerAddress);
export const useSelectedClusterId = () => useAtom(selectedClusterId);

export const useZoneOverlayPolygons = () => useAtom(zoneOverlayPolygons);
export const useSelectedPolygonBoundsOnMap = () => useAtom(selectedPolygonBoundsOnMap);
export const useSelectedPolygonId = () => useAtom(selectedPolygonId);
export const useShowAllProjectsWithinPolygon = () =>
  useAtom(showAllProjectsWithinPolygon);
export const useIsPolygonDrawingEnabled = () => useAtom(isPolygonDrawingEnabledAtom);
export const useHasPlacedFirstPolygonDrawingPoint = () =>
  useAtom(hasPlacedFirstPolygonDrawingPointAtom);
export const useProjectInsightsViewType = () => useAtom(projectInsightsViewTypeAtom);
export const useAddressSearch = () => useAtom(addressSearchAtom);
export const useAppliedMapGroupId = () => useAtom(appliedMapGroupIdAtom);

export const useMapProperties = () => {
  const [, setMapZoom] = useMapZoom();
  const [, setMapCenter] = useMapCenter();

  const updateMapCenter = useCallback(
    (coordinates: google.maps.LatLng | google.maps.LatLngLiteral) => {
      setMapCenter(coordinates as google.maps.LatLngLiteral);
    },
    [setMapCenter],
  );

  const updateMapZoom = useCallback(
    (zoomLevel: number) => {
      setMapZoom(zoomLevel);
    },
    [setMapZoom],
  );

  return {
    updateMapCenter,
    updateMapZoom,
  };
};

export const useMapState = () => {
  const selectedMapType = useAtomValue(selectedMapTypeAtom);
  const setSelectedMapType = useSetAtom(selectedMapTypeAtom);
  const mapLoaded = useAtomValue(mapApiLoaded);
  const updateMapLoaded = useSetAtom(mapApiLoaded);
  const pinsLoading = useAtomValue(loading);
  const availablePolygons = useAtomValue(polygons);
  const updateAvailablePolygons = useSetAtom(polygons);
  const availablePolygonLiterals = useAtomValue(polygonLiterals);
  const updateAvailablePolygonLiterals = useSetAtom(polygonLiterals);
  const selectedPolygons = useAtomValue(selectedPolygonsList);
  const updateSelectedPolygons = useSetAtom(selectedPolygonsList);
  const clusterPointsList = useAtomValue(clusterPoints);
  const updateClusterPointsList = useSetAtom(clusterPoints);
  const mapRef = useAtomValue(mapReference);
  const updateMapRef = useSetAtom(mapReference);
  const projects = useAtomValue(projectList);
  const updateProjects = useSetAtom(projectList);
  const parcelProjects = useAtomValue(parcelProjectsAtom);
  const updateParcelProjects = useSetAtom(parcelProjectsAtom);
  const parcelProjectsLoading = useAtomValue(parcelProjectsLoadingAtom);
  const updateParcelProjectsLoading = useSetAtom(parcelProjectsLoadingAtom);
  const parcelPolygons = useAtomValue(parcelPolygonsAtom);
  const updateParcelPolygons = useSetAtom(parcelPolygonsAtom);
  const updateForceRefreshPolygons = useSetAtom(forceRefreshPolygonsAtom);
  const scrollable = useAtomValue(isScrollable);
  const updateScrollable = useSetAtom(isScrollable);

  const updateCenter = useCallback(
    (coordinates: google.maps.LatLng | google.maps.LatLngLiteral) => {
      mapRef?.panTo(coordinates);
    },
    [mapRef],
  );

  return useMemo(() => {
    return {
      selectedMapType,
      setSelectedMapType,
      mapLoaded,
      updateMapLoaded,
      updateCenter,
      pinsLoading,
      availablePolygons,
      updateAvailablePolygons,
      availablePolygonLiterals,
      updateAvailablePolygonLiterals,
      selectedPolygons,
      updateSelectedPolygons,
      clusterPointsList,
      updateClusterPointsList,
      mapRef,
      updateMapRef,
      projects,
      updateProjects,
      parcelProjects,
      updateParcelProjects,
      parcelProjectsLoading,
      updateParcelProjectsLoading,
      parcelPolygons,
      updateParcelPolygons,
      updateForceRefreshPolygons,
      scrollable,
      updateScrollable,
    };
  }, [
    selectedMapType,
    setSelectedMapType,
    mapLoaded,
    updateMapLoaded,
    updateCenter,
    pinsLoading,
    availablePolygons,
    updateAvailablePolygons,
    availablePolygonLiterals,
    updateAvailablePolygonLiterals,
    selectedPolygons,
    updateSelectedPolygons,
    clusterPointsList,
    updateClusterPointsList,
    mapRef,
    updateMapRef,
    projects,
    updateProjects,
    parcelProjects,
    updateParcelProjects,
    parcelProjectsLoading,
    updateParcelProjectsLoading,
    parcelPolygons,
    updateParcelPolygons,
    updateForceRefreshPolygons,
    scrollable,
    updateScrollable,
  ]);
};

export const useMapRadius = () => {
  const radius = useAtomValue(mapRadius);
  const updateRadius = useSetAtom(mapRadius);

  return useMemo(() => {
    return {
      radius,
      updateRadius,
    };
  }, [radius, updateRadius]);
};

export const useGlobalGoogleMapPin = () => {
  const googleMapPin = useAtomValue(mapPin);
  const updateGoogleMapPin = useSetAtom(mapPin);

  return useMemo(() => {
    return {
      googleMapPin,
      updateGoogleMapPin,
    };
  }, [googleMapPin, updateGoogleMapPin]);
};
